/**
 * LongerRoadPoster.js — a single-frame WhatsApp teaser.
 *
 * One phone-height frame: chips, title, the poem, an illustrated convoy
 * strip, and the Request-to-book CTA. That's it. The full itinerary,
 * pricing, and included list live on /itineraries/longer-road.
 *
 * The downloadable JPG is a snapshot of exactly this frame — so the
 * on-screen teaser and the shareable image are the same object.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mountain, Download, ArrowRight } from 'lucide-react';
import LongerRoadRequestModal from '../components/LongerRoadRequestModal';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const AMBER = '#EBC773';
const WA_GREEN = '#25D366';
const IMG_HERO = 'https://gohimalayan.com/tirthan_hero.jpg';
const FULL_URL = 'gohimalayan.com/itineraries/longer-road';

/** The illustrated SUV convoy strip · four vehicles on a mountain road */
const ConvoyStrip = ({ className = '' }) => (
  <svg viewBox="0 0 1200 220" preserveAspectRatio="xMidYMid slice" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient id="stripDawn" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8b6a48" stopOpacity="0.85"/>
        <stop offset="0.6" stopColor="#3a4c66" stopOpacity="0.85"/>
        <stop offset="1" stopColor="#101a2a" stopOpacity="0.95"/>
      </linearGradient>
      <linearGradient id="stripPeaksFar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#3d5773"/><stop offset="1" stopColor="#243a55"/></linearGradient>
      <linearGradient id="stripPeaksMid" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1e2f45"/><stop offset="1" stopColor="#0d1a2b"/></linearGradient>
      <linearGradient id="stripPeaksNear" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#080f1a"/><stop offset="1" stopColor="#040711"/></linearGradient>
      <g id="suvSil">
        <ellipse cx="222" cy="74" rx="30" ry="10" fill="#ffd88a" opacity="0.55"/>
        <path d="M 18 82 L 18 62 Q 18 56 24 56 L 46 56 L 60 32 Q 65 26 74 26 L 158 26 Q 168 26 174 32 L 190 56 L 214 56 Q 220 56 220 62 L 220 82 Z" fill="#050a15"/>
        <path d="M 72 32 L 154 32 Q 162 32 165 38 L 175 54 L 60 54 L 68 38 Q 71 32 72 32 Z" fill="rgba(255,220,140,0.22)"/>
        <rect x="60" y="18" width="118" height="6" rx="1.5" fill="#050a15"/>
        <rect x="86" y="10" width="16" height="12" rx="1" fill="#050a15"/>
        <circle cx="55" cy="86" r="16" fill="#050a15"/>
        <circle cx="185" cy="86" r="16" fill="#050a15"/>
        <circle cx="216" cy="70" r="3.5" fill="#fff5c8"/>
      </g>
    </defs>
    <rect width="1200" height="220" fill="url(#stripDawn)"/>
    <path d="M 0 120 L 90 60 L 190 100 L 300 40 L 420 100 L 550 55 L 700 110 L 850 55 L 990 105 L 1100 65 L 1200 100 L 1200 220 L 0 220 Z" fill="url(#stripPeaksFar)"/>
    <path d="M 285 55 L 300 40 L 315 60 Z" fill="#e0e6ea" opacity="0.75"/>
    <path d="M 540 68 L 550 55 L 562 70 Z" fill="#e0e6ea" opacity="0.75"/>
    <path d="M 840 68 L 850 55 L 862 70 Z" fill="#e0e6ea" opacity="0.75"/>
    <path d="M 0 160 L 130 100 L 260 150 L 400 100 L 560 155 L 720 105 L 880 155 L 1050 100 L 1200 145 L 1200 220 L 0 220 Z" fill="url(#stripPeaksMid)"/>
    {[80,145,220,295,370,445,520,600,680,760,830,910,995,1075,1160].map((x,i)=>{
      const y = 140 + ((i*7)%14);
      return (<path key={i} d={`M ${x} ${y} l -5 10 l 3 0 l -4 8 l 2 0 l -5 10 l 18 0 l -5 -10 l 2 0 l -4 -8 l 3 0 Z`} fill="#050b18" opacity="0.9"/>);
    })}
    <path d="M 0 200 L 120 175 L 260 190 L 420 170 L 580 190 L 740 170 L 900 190 L 1060 170 L 1200 190 L 1200 220 L 0 220 Z" fill="url(#stripPeaksNear)"/>
    <path d="M 0 210 Q 200 190 400 200 T 800 190 T 1200 195" stroke="#c9a26e" strokeWidth="3.5" fill="none" opacity="0.9"/>
    <path d="M 0 210 Q 200 190 400 200 T 800 190 T 1200 195" stroke="rgba(255,255,255,0.42)" strokeWidth="0.8" fill="none" strokeDasharray="12 12"/>
    <g transform="translate(160, 168) scale(0.30)"><use href="#suvSil"/></g>
    <g transform="translate(440, 168) scale(0.28)"><use href="#suvSil"/></g>
    <g transform="translate(720, 168) scale(0.24)"><use href="#suvSil"/></g>
    <g transform="translate(970, 170) scale(0.20)"><use href="#suvSil"/></g>
  </svg>
);

export default function LongerRoadPoster() {
  const [requestOpen, setRequestOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0b1220] text-white" data-testid="longer-road-poster">
      {/* THE POSTER FRAME · everything visible on one phone screen */}
      <section id="poster-frame" className="relative min-h-[100svh] flex flex-col overflow-hidden">
        {/* Valley photo backdrop, dimmed */}
        <img src={IMG_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.42) saturate(1.1)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,18,32,0.7) 0%, rgba(11,18,32,0.35) 35%, rgba(11,18,32,0.75) 100%)' }} />

        {/* Content wrapper */}
        <div className="relative flex-1 flex flex-col justify-between px-5 sm:px-8 pt-8 pb-6 max-w-[500px] mx-auto w-full text-center">

          {/* Top · brand + chips */}
          <div>
            <div className="flex items-center justify-center gap-2 mb-5">
              <Mountain className="w-4 h-4" style={{ color: AMBER }} />
              <span className="text-[11px] font-bold tracking-tight text-white" style={pf}>GoHimalayan</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              <span className="text-[9px] tracking-[0.35em] uppercase px-3 py-1 rounded-full border" style={{ borderColor: `${AMBER}80`, color: AMBER }}>
                A selected invite
              </span>
              <span className="text-[9px] tracking-[0.35em] uppercase px-3 py-1 rounded-full inline-flex items-center gap-1.5" style={{ backgroundColor: AMBER, color: '#0f1620' }}>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-stone-900" /> SUV / MUV &middot; Self-driven
              </span>
            </div>
            <h1 className="text-[38px] sm:text-[52px] leading-[0.98] mb-1 text-white" style={pf}>The Longer Road</h1>
            <p className="text-[20px] sm:text-[26px] italic leading-tight" style={{ ...pf, color: AMBER }}>
              Tirthan Valley &middot; October 2026
            </p>
          </div>

          {/* Middle · the poem */}
          <div className="my-6">
            <div className="text-[15px] sm:text-[18px] leading-[1.6] italic text-white space-y-3" style={pf}>
              <p>For anyone who&rsquo;s ever cancelled a plan<br />just to go drive somewhere quieter.</p>
              <p>For couples who talk about mountains<br />the way other people talk about holidays.</p>
              <p>For the ones we&rsquo;d share a fire with &mdash;<br />yes, you.</p>
              <p className="text-[17px] sm:text-[20px] pt-1" style={{ color: AMBER }}>
                The mountains are calling.<br />
                We&rsquo;re picking up in twelves.
              </p>
            </div>
          </div>

          {/* Convoy strip */}
          <div className="rounded-lg overflow-hidden border border-white/10 my-2">
            <ConvoyStrip className="block w-full h-[86px] sm:h-[110px]" />
          </div>

          {/* Bottom · CTA */}
          <div className="mt-6">
            <button onClick={() => setRequestOpen(true)}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-[15px] shadow-2xl shadow-emerald-900/40 hover:brightness-105 transition whitespace-nowrap"
                    style={{ backgroundColor: WA_GREEN, color: 'white' }}
                    data-testid="poster-request-hero">
              <MessageCircle className="w-5 h-5" /> Request to book
            </button>
            <p className="text-[10px] text-amber-200/85 tracking-[0.3em] uppercase mt-3">
              12 couples &middot; Early bird &#8377;42,000 &middot; Closes 10 Sept
            </p>
            <p className="text-[11px] text-white/60 mt-4 italic" style={pf}>
              {FULL_URL}
            </p>
          </div>
        </div>
      </section>

      {/* Utility bar — hidden inside the poster render */}
      <section id="poster-utility" className="py-8 px-5 bg-stone-950 text-stone-500 print:hidden">
        <div className="max-w-2xl mx-auto text-center space-y-3">
          <div className="flex flex-wrap justify-center gap-2">
            <a href="/tirthan-teaser.jpg" download="TheLongerRoad-Tirthan.jpg"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[13px] transition"
               style={{ backgroundColor: AMBER, color: '#0f1620' }}
               data-testid="poster-download-jpg">
              <Download className="w-4 h-4" /> Download this poster
            </a>
            <Link to="/itineraries/longer-road"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-[13px] font-medium transition">
              Read the full brief <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-[10px] text-stone-500 italic max-w-md mx-auto leading-relaxed pt-1">
            Save the image and drop it into any WhatsApp chat &mdash; it opens inline.
          </p>
        </div>
      </section>

      {/* Request-to-book modal */}
      <LongerRoadRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
